import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { formations, type FormationLevel, type FormationFormat } from "@/mocks/formations";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { useJsonLd } from "@/hooks/useJsonLd";

const levelColors: Record<string, string> = {
  "Débutant":      "bg-green-50 text-green-700 border-green-200",
  "Intermédiaire": "bg-amber-50 text-amber-700 border-amber-200",
  "Avancé":        "bg-red-50 text-red-600 border-red-200",
};

export default function FormationsPage() {
  const { t } = useTranslation();
  const siteUrl = import.meta.env.VITE_SITE_URL || "https://example.com";
  const [search, setSearch]         = useState("");
  const [level, setLevel]           = useState<FormationLevel | "all">("all");
  const [format, setFormat]         = useState<FormationFormat | "all">("all");
  const [onlyWazi, setOnlyWazi]     = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useSeoMeta({
    title: "Formations pour entrepreneurs africains — Wazi Stories Afrique",
    description: "Découvrez les meilleures formations en ligne et présentiel pour entrepreneurs africains. Développez vos compétences en business, fintech, agritech et innovation.",
    type: "website",
    keywords: "formation entrepreneur africain, formation business Afrique, formation en ligne Afrique, Wazi Stories formations",
  });

  useJsonLd({
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Formations pour entrepreneurs africains",
    "description": "Sélection de formations pour entrepreneurs africains en ligne et en présentiel",
    "url": `${siteUrl}/formations`,
    "numberOfItems": formations.length,
    "itemListElement": formations.slice(0, 10).map((f, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "item": {
        "@type": "Course",
        "name": f.title,
        "description": f.description,
        "provider": {
          "@type": "Organization",
          "name": f.provider
        },
        "educationalLevel": f.level,
        "courseMode": f.format === "En ligne" ? "online" : f.format === "Présentiel" ? "onsite" : "blended",
        "timeRequired": f.duration,
        "url": f.link
      }
    }))
  }, "formations");

  const levelKeyMap: Record<string, string> = {
    "Débutant":      "formations.level.beginner",
    "Intermédiaire": "formations.level.intermediate",
    "Avancé":        "formations.level.advanced",
  };

  const filtered = useMemo(() => {
    let data = formations;
    if (search) {
      const q = search.toLowerCase();
      data = data.filter(
        (f) =>
          f.title.toLowerCase().includes(q) ||
          f.provider.toLowerCase().includes(q) ||
          f.sector.toLowerCase().includes(q)
      );
    }
    if (level !== "all")  data = data.filter((f) => f.level === level);
    if (format !== "all") data = data.filter((f) => f.format === format);
    if (onlyWazi)         data = data.filter((f) => f.partnerWazi);
    return data;
  }, [search, level, format, onlyWazi]);

  const activeFiltersCount = [level !== "all", format !== "all", onlyWazi].filter(Boolean).length;

  const resetFilters = () => { setLevel("all"); setFormat("all"); setOnlyWazi(false); };

  return (
    <div className="min-h-screen bg-[#fdf8f3]">
      <Navbar />

      {/* Hero */}
      <div className="pt-16 md:pt-20 bg-gradient-to-br from-orange-600 to-amber-500 pb-8 md:pb-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 pt-8 md:pt-12">
          <div className="text-center mb-6 md:mb-10">
            <div className="inline-flex items-center gap-2 bg-white/20 text-white text-xs font-bold px-4 py-2 rounded-full mb-3 md:mb-4">
              <i className="ri-graduation-cap-line"></i>{t("formations.hero.badge")}
            </div>
            <h1 className="text-2xl md:text-5xl font-bold text-white font-serif mb-2 md:mb-3">{t("formations.hero.title")}</h1>
            <p className="text-white/80 text-sm md:text-base max-w-xl mx-auto">{t("formations.hero.subtitle")}</p>
          </div>

          {/* Wazi partner highlight */}
          <div className="max-w-3xl mx-auto bg-white/15 backdrop-blur-sm rounded-2xl p-4 md:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 border border-white/20">
            <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-white/20 flex-shrink-0">
              <i className="ri-star-fill text-yellow-300 text-xl md:text-2xl"></i>
            </div>
            <div className="flex-1">
              <p className="text-white font-bold text-sm">{t("formations.wazi.title")}</p>
              <p className="text-white/70 text-xs">{t("formations.wazi.desc")}</p>
            </div>
            <button
              onClick={() => { setOnlyWazi(true); setFiltersOpen(true); }}
              className="flex-shrink-0 bg-white text-orange-600 font-bold text-sm px-4 md:px-5 py-2 md:py-2.5 rounded-full hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t("formations.wazi.cta")}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-6 md:py-10">

        {/* Filters panel */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4 md:p-5 mb-6 md:mb-8">
          <div className="relative">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("formations.search.placeholder")}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-orange-400 transition-colors"
            />
          </div>

          {/* Toggle on mobile */}
          <div className="flex items-center justify-between mt-3 md:hidden">
            <span className="text-xs text-gray-500">
              {activeFiltersCount > 0
                ? `${activeFiltersCount} ${activeFiltersCount > 1 ? t("formations.filter.active_plural") : t("formations.filter.active")}`
                : t("formations.filter.hint")}
            </span>
            <button
              onClick={() => setFiltersOpen((v) => !v)}
              className="flex items-center gap-1.5 text-xs font-semibold text-orange-500 cursor-pointer"
            >
              <i className="ri-filter-3-line"></i>
              {filtersOpen ? t("formations.filter.hide") : t("formations.filter.toggle")}
              {activeFiltersCount > 0 && (
                <span className="w-4 h-4 flex items-center justify-center rounded-full bg-orange-500 text-white text-xs font-bold">
                  {activeFiltersCount}
                </span>
              )}
            </button>
          </div>

          {/* Advanced filters */}
          <div className={`${filtersOpen ? "flex" : "hidden"} md:flex flex-col md:flex-row gap-3 mt-3 flex-wrap`}>
            <select
              value={level}
              onChange={(e) => setLevel(e.target.value as FormationLevel | "all")}
              className="flex-1 min-w-[140px] border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400 bg-white cursor-pointer"
            >
              <option value="all">{t("formations.filter.allLevels")}</option>
              <option value="Débutant">{t("formations.level.beginner")}</option>
              <option value="Intermédiaire">{t("formations.level.intermediate")}</option>
              <option value="Avancé">{t("formations.level.advanced")}</option>
            </select>
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value as FormationFormat | "all")}
              className="flex-1 min-w-[140px] border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400 bg-white cursor-pointer"
            >
              <option value="all">{t("formations.filter.allFormats")}</option>
              <option value="En ligne">{t("formations.filter.online")}</option>
              <option value="Présentiel">{t("formations.filter.inPerson")}</option>
              <option value="Hybride">{t("formations.filter.hybrid")}</option>
            </select>
            <div className="flex flex-wrap gap-3 items-center">
              <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                <input type="checkbox" checked={onlyWazi} onChange={(e) => setOnlyWazi(e.target.checked)} className="w-4 h-4 accent-orange-500" />
                <span className="text-sm text-gray-700 font-medium flex items-center gap-1">
                  <i className="ri-star-fill text-orange-400 text-xs"></i>{t("formations.filter.wazi")}
                </span>
              </label>
              {activeFiltersCount > 0 && (
                <button
                  onClick={resetFilters}
                  className="text-xs font-semibold text-orange-500 border border-orange-200 px-3 py-1.5 rounded-full hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t("formations.filter.reset")}
                </button>
              )}
            </div>
          </div>
        </div>

        <p className="text-sm text-gray-500 mb-4 md:mb-6">
          {filtered.length} {filtered.length > 1 ? t("formations.results_plural") : t("formations.results")}
        </p>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {filtered.map((f) => (
            <a
              key={f.id}
              href={f.link.startsWith("/") ? undefined : f.link}
              target={f.link.startsWith("/") ? undefined : "_blank"}
              rel={f.link.startsWith("/") ? undefined : "nofollow noopener noreferrer"}
              className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-orange-200 hover:-translate-y-1 transition-all duration-300 cursor-pointer block"
            >
              <div className="relative h-40 md:h-44 overflow-hidden">
                <img src={f.image} alt={f.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
                  {f.partnerWazi && (
                    <span className="bg-orange-500 text-white text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
                      <i className="ri-star-fill text-yellow-300 text-xs"></i>Wazi
                    </span>
                  )}
                  {f.certification && (
                    <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-medium px-2.5 py-1 rounded-full">
                      {t("formations.certified")}
                    </span>
                  )}
                </div>
                <div className="absolute bottom-3 left-3 flex items-center gap-2">
                  <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg overflow-hidden bg-white flex-shrink-0">
                    <img src={f.providerLogo} alt={f.provider} className="w-full h-full object-cover" />
                  </div>
                  <span className="text-white text-xs font-semibold">{f.provider}</span>
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${levelColors[f.level]}`}>
                    {t(levelKeyMap[f.level] ?? f.level)}
                  </span>
                  <span className="text-xs text-gray-400 flex items-center gap-1"><i className="ri-time-line"></i>{f.duration}</span>
                  <span className="text-xs text-gray-400">{f.format}</span>
                </div>
                <h3 className="font-bold text-gray-900 text-sm leading-snug line-clamp-2 mb-2 group-hover:text-orange-500 transition-colors">{f.title}</h3>
                <p className="text-xs text-gray-500 line-clamp-2 mb-3">{f.description}</p>
                <div className="flex flex-wrap gap-1 mb-3">
                  {f.skills.slice(0, 3).map((s) => (
                    <span key={s} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{s}</span>
                  ))}
                </div>
                <div className="flex items-center justify-end pt-3 border-t border-gray-100">
                  <span className="text-orange-500 text-xs font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                    {t("formations.register")} <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16 md:py-20 text-gray-400">
            <i className="ri-graduation-cap-line text-5xl mb-4 block"></i>
            <p className="text-lg font-medium">{t("formations.noResults")}</p>
          </div>
        )}

        {/* CTA */}
        <div className="mt-8 md:mt-12 bg-gray-900 rounded-2xl p-6 md:p-8 text-center">
          <h3 className="text-white font-bold text-lg md:text-xl mb-2">{t("formations.cta.title")}</h3>
          <p className="text-gray-400 text-sm mb-4 md:mb-5">{t("formations.cta.desc")}</p>
          <a
            href="mailto:contact@waziconsulting.com"
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-5 md:px-6 py-2.5 md:py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm"
          >
            <i className="ri-mail-send-line"></i>{t("formations.cta.btn")}
          </a>
        </div>
      </div>
      <Footer />
    </div>
  );
}
